/* eslint-disable react/no-this-in-sfc */
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Checkbox } from '../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { Dot } from '../../../../../CommonComponents/OPL/Dot/Dot';
import { HtmlTable } from '../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { LineChart } from '../../../../../CommonComponents/OPL/charts/LineChart/LineChart';
import { formatPercentage } from '../../../../../CommonUtils/formatNumber';
import { useIsEmbeddedMobile } from '../../../../../_hooks/useIsEmbeddedMobile';

const CHART_HEIGHT = 340;

interface Props {
  setSelectedDate: (date: string | null) => void;
  data: {
    name: string;
    data: number[][];
    color: string;
  }[];
  isBenchmarkHidden: (name: string) => boolean;
  toggleBenchmarkVisibility: (name: string) => void;
}

export const PerformanceVsBenchmarkChartContent = ({
  setSelectedDate,
  data,
  isBenchmarkHidden,
  toggleBenchmarkVisibility,
}: Props) => {
  const isEmbeddedMobile = useIsEmbeddedMobile();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const filteredData = useMemo(() => {
    if (!data) {
      return null;
    }

    return data.map(benchmark => ({ ...benchmark, visible: !isBenchmarkHidden(benchmark.name) }));
  }, [data, isBenchmarkHidden]);

  return (
    <>
      {filteredData && (
        <MemoLineChart
          data={filteredData}
          isEmbeddedMobile={isEmbeddedMobile}
          setSelectedIndex={setSelectedIndex}
          setSelectedDate={setSelectedDate}
        />
      )}
      {isEmbeddedMobile && (
        <Box px="16" pt="12">
          <HtmlTable variant="embedded" edgePadding={0} style={{ border: 'none' }}>
            <table>
              <tbody>
                <tr style={{ border: 'none' }}>
                  <td>
                    <Text fontSize="h6" fontWeight={600}>
                      Return
                    </Text>
                  </td>

                  <td>
                    <Text fontSize="h6" fontWeight={600} textAlign="right">
                      {formatPercentage(data[0].data[selectedIndex ?? data[0].data.length - 1][1], {
                        mantissa: 2,
                      })}
                    </Text>
                  </td>

                  <td />
                </tr>

                {data.slice(1).map(benchmark => (
                  <tr key={benchmark.name} style={{ border: 'none' }}>
                    <td css="display: flex; align-items: center">
                      <Dot bg={benchmark.color} mr="8" />

                      <Text fontWeight={500}>{benchmark.name}</Text>
                    </td>

                    <td>
                      <Text fontWeight={500} variant="tabular" textAlign="right">
                        {formatPercentage(
                          benchmark.data[selectedIndex ?? benchmark.data.length - 1][1],
                          {
                            mantissa: 2,
                          },
                        )}
                      </Text>
                    </td>

                    <td>
                      <Checkbox
                        checked={!isBenchmarkHidden(benchmark.name)}
                        onChange={() => {
                          toggleBenchmarkVisibility(benchmark.name);
                        }}
                        aria-label="Toggle benchmark visibility"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HtmlTable>
        </Box>
      )}
    </>
  );
};

interface MemoLineChartProps {
  data: { name: string; data: number[][]; color: string }[];
  isEmbeddedMobile: boolean;
  setSelectedIndex: (index: number | null) => void;
  setSelectedDate: (date: string | null) => void;
}

const MemoLineChart = React.memo(
  ({ data, isEmbeddedMobile, setSelectedIndex, setSelectedDate }: MemoLineChartProps) => {
    return (
      <LineChart
        series={data}
        customSettings={{
          legend: {
            enabled: !isEmbeddedMobile,
            y: 6,
            align: 'center',
          },
          xAxis: {
            crosshair: isEmbeddedMobile
              ? {
                  width: 2,
                  dashStyle: 'shortdot',
                }
              : false,
            tickPixelInterval: isEmbeddedMobile ? 85 : 100,
            labels: {
              y: 28,
            },
          },
          yAxis: {
            minTickInterval: 0.0001,
          },
          chart: {
            height: CHART_HEIGHT,
          },
          tooltip: {
            shared: isEmbeddedMobile,
            style: {
              opacity: isEmbeddedMobile ? 0 : 1,
            },
            formatter() {
              if (isEmbeddedMobile) {
                setSelectedIndex(data[0].data.findIndex(([time]) => this.x === time));
                setSelectedDate(moment(this.x).format('MM/DD/YYYY'));
                return null;
              }

              let tooltip = `
                <span style="font-size: 10px">
                  ${moment(this.key).format('ll')}
                </span>
                <br />
              `;

              tooltip += `
                <span style="color:${this.color}">
                  \u25CF
                </span>
        
                ${this.series.name}: 
        
                <span style="font-weight: 600; margin-left: 4px">
                  ${formatPercentage(Number(this.y), { decimalPlaces: 2 })}
                </span>
              `;

              return tooltip;
            },
          },
          plotOptions: {
            series: {
              events: {
                mouseOut() {
                  this.chart.tooltip.hide();
                  setSelectedIndex(null);
                  setSelectedDate(null);
                },
              },
            },
          },
        }}
        xAxisType="datetime"
        yAxisType="percentage"
      />
    );
  },
);
